import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Banner, Button, Card, Chip, Details, List, ListItem, Select, TopAppBar } from '@meshble/molo';
import { api, must } from '../lib/api';
import { FAMIGLIE, chiVoce, descriviVoce, nomeVoce, oraDi, perGiorno, type Voce } from '../lib/registro';
import { CaricoSezione } from '../brand';
import { BarraUtente } from '../shell';
import { localeIntl, t } from '../lib/lingua';

/** Righe per pagina: abbastanza da riempire lo schermo, poche da arrivare subito. */
const PAGINA = 50;

/** Cosa si sta guardando, per il titolo del filtro: «questo server». */
function questaRisorsa(r: string | undefined): string {
  const nomi: Record<string, string> = {
    server: t('questo server'), instance: t('questa istanza'), domain: t('questo dominio'), backup: t('questo backup'),
    membership: t('questa persona'), organization: t('l’organizzazione'), org: t('l’organizzazione'),
    cloud_credential: t('questo progetto'), invitation: t('questo invito'), user: t('questo utente'),
  };
  return nomi[r ?? ''] ?? t('questa risorsa');
}

/**
 * Il registro: chi ha fatto cosa.
 *
 * Ogni operazione che cambia qualcosa scrive una riga nella stessa
 * transazione del cambiamento, e ogni riga porta l'hash della precedente. Un
 * giro notturno ricalcola la catena: togliere o modificare una riga a
 * posteriori si vede.
 *
 * Non è una schermata da guardare tutti i giorni. È quella che si apre dopo,
 * quando bisogna ricostruire come si è arrivati qui — e le domande sono
 * sempre le stesse tre: cosa è successo a questo server, cosa ha fatto
 * questa persona, cosa è successo di questo tipo. I filtri sono quelle.
 * Le righe arrivano a pagine col cursore, dal più recente: un registro
 * cresce per sempre, e la pagina mille deve costare quanto la prima.
 */
export function RegistroPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/registro' });
  const filtri = useSearch({ from: '/shell/orgs/$slug/registro' });
  const navigate = useNavigate();
  const famiglia = FAMIGLIE.find((f) => f.chiave === filtri.cosa);

  const q = useInfiniteQuery({
    queryKey: ['audit', slug, filtri],
    queryFn: ({ pageParam }) =>
      must(api.GET('/api/v1/orgs/{slug}/audit', {
        params: {
          path: { slug },
          query: {
            limit: PAGINA,
            ...(pageParam ? { before: pageParam } : {}),
            ...(filtri.risorsa ? { resource: filtri.risorsa } : {}),
            ...(filtri.id ? { resourceId: filtri.id } : {}),
            ...(filtri.chi ? { actor: filtri.chi } : {}),
            ...(famiglia ? { action: famiglia.prefissi } : {}),
          },
        },
      })),
    initialPageParam: 0,
    // Meno di una pagina piena vuol dire che è finita; altrimenti il seq
    // dell'ultima riga è il cursore della prossima.
    getNextPageParam: (ultima) => (ultima && ultima.length === PAGINA ? ultima[ultima.length - 1]!.seq : undefined),
  });
  const voci: Voce[] = q.data?.pages.flatMap((p) => p ?? []) ?? [];

  // Le persone, per il filtro «Chi». Chi legge il registro vede già i membri.
  const membri = useQuery({
    queryKey: ['members', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/members', { params: { path: { slug } } })),
  });

  const cambia = (patch: Partial<typeof filtri>) =>
    navigate({ to: '/orgs/$slug/registro', params: { slug }, search: pulisci({ ...filtri, ...patch }), replace: true });

  const nomeRisorsa = filtri.id ? (voci[0] ? nomeVoce(voci[0]) : '') : '';
  const cosaRisorsa = questaRisorsa(filtri.risorsa);

  return (
    <>
      <TopAppBar title={t('Registro')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack">
          <div className="md-registro__filtri">
            <div className="md-registro__famiglie" role="group" aria-label={t('Cosa')}>
              <Chip selected={!filtri.cosa} onClick={() => cambia({ cosa: undefined })}>{t('Tutto')}</Chip>
              {FAMIGLIE.map((f) => (
                <Chip key={f.chiave} selected={filtri.cosa === f.chiave} onClick={() => cambia({ cosa: f.chiave })}>
                  {t(f.label)}
                </Chip>
              ))}
            </div>
            <Select
              label=""
              aria-label={t('Chi')}
              className="md-registro__chi"
              value={filtri.chi ?? ''}
              onChange={(e) => cambia({ chi: e.target.value || undefined })}
              options={[
                { value: '', label: t('Chiunque') },
                ...(membri.data?.members ?? []).map((m) => ({ value: m.userId, label: m.name ? `${m.name} (${m.email})` : m.email })),
              ]}
            />
          </div>

          {filtri.id && (
            // Il link «Registro di questo server» arriva qui: si dice cosa si
            // sta guardando, e si può tornare a tutto con un clic.
            <Banner
              title={nomeRisorsa
                ? t('Solo {cosa}: «{nome}»', { cosa: cosaRisorsa, nome: nomeRisorsa })
                : t('Solo {cosa}', { cosa: cosaRisorsa })}
              action={<Button variant="text" onClick={() => cambia({ risorsa: undefined, id: undefined })}>{t('Tutto il registro')}</Button>}
            >
              {t('Le righe che la riguardano, dalla più recente.')}
            </Banner>
          )}

          {q.error && (
            <Banner tone="error" title={t('Non riesco a leggere il registro')}>
              {q.error.message} {t('Serve il permesso di lettura del registro, che non è di tutti.')}
            </Banner>
          )}

          {q.isSuccess && voci.length === 0 && (
            <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
              {filtri.cosa || filtri.chi || filtri.id ? t('Niente con questi filtri.') : t('Ancora niente da raccontare.')}
            </p>
          )}

          {voci.length > 0 && (
            <Card variant="outlined">
              {perGiorno(voci).map((g) => (
                <section key={g.giorno} className="md-registro__giorno">
                  <h3 className="md-title-small">{g.giorno}</h3>
                  <List>
                    {g.voci.map((v) => <Riga key={v.seq} voce={v} />)}
                  </List>
                </section>
              ))}
              {q.hasNextPage && (
                <div className="md-registro__altre">
                  <Button variant="tonal" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
                    {q.isFetchingNextPage ? t('Carico…') : t('Mostra le righe prima')}
                  </Button>
                </div>
              )}
              <Details summary={t('Come si legge')}>
                <p className="md-body-small" style={{ margin: 0 }}>
                  {t('Le righe sono in ordine dal più recente. Ognuna è stata scritta nella stessa transazione dell’operazione che descrive: se l’operazione è avvenuta, la riga c’è. Un clic su una riga apre com’era prima e com’è dopo.')}
                </p>
                <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {t('Ogni riga porta l’hash della precedente. Un giro notturno ricalcola tutta la catena e segnala da quale punto non torna: togliere o modificare una riga a posteriori si vede. Non protegge da chi può riscrivere l’intero registro, ma da chi vuole togliere la propria riga sì.')}
                </p>
              </Details>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/** I filtri senza le chiavi vuote: l'URL non porta `?chi=` a vuoto. */
function pulisci(f: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(f).filter((kv): kv is [string, string] => !!kv[1]));
}

/**
 * Una riga: l'ora, la frase, chi. Un clic apre il dettaglio — il codice, la
 * risorsa, gli snapshot — che serve a chi ricostruisce, non a chi scorre.
 */
function Riga({ voce: v }: { voce: Voce }) {
  const [aperta, setAperta] = useState(false);
  return (
    <>
      <ListItem
        interactive
        aria-expanded={aperta}
        icon={<span className="md-registro__ora md-mono">{oraDi(v.at)}</span>}
        headline={descriviVoce(v)}
        supporting={chiVoce(v)}
        onClick={() => setAperta((a) => !a)}
      />
      {aperta && (
        <li className="md-registro__dettaglio">
          <dl className="md-dati">
            <div className="md-dato"><dt>{t('Codice')}</dt><dd className="md-mono">{v.action}</dd></div>
            {v.resource && <div className="md-dato"><dt>{t('Risorsa')}</dt><dd className="md-mono">{v.resource} {v.resourceId}</dd></div>}
            <div className="md-dato"><dt>{t('Quando')}</dt><dd>{new Date(v.at).toLocaleString(localeIntl())}</dd></div>
            <div className="md-dato"><dt>{t('Riga')}</dt><dd className="md-mono">#{v.seq}</dd></div>
          </dl>
          {v.before && <Snapshot titolo={t('Prima')} json={v.before} />}
          {v.after && <Snapshot titolo={t('Dopo')} json={v.after} />}
        </li>
      )}
    </>
  );
}

function Snapshot({ titolo, json }: { titolo: string; json: string }) {
  let testo = json;
  try {
    testo = JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    // Non è JSON: si mostra com'è, che è comunque quello che l'hub ha scritto.
  }
  return (
    <div className="md-registro__snapshot">
      <span className="md-label-medium">{titolo}</span>
      <pre className="md-code">{testo}</pre>
    </div>
  );
}
